import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Latest AI Scan hook. Every Beauty module (Makeup, Hair & Beard, Care Point,
 * Routine) relies on the ONE scan on file — no re-uploads.
 *
 * SHARED CACHE — the same scan object is reused across all Beauty modules
 * within one session so navigating from AI-Scan → Makeup Studio → Hair &
 * Beard doesn't flash the "no scan yet" empty state on each mount (which
 * caused the "loop asking for scan" bug reported on mobile where the
 * initial network fetch would sometimes finish AFTER the empty-state
 * render).
 *
 * `refresh()` still hits the API and updates all mounted instances via the
 * shared subscriber list. `setScan(scanObj)` (returned from useState) also
 * updates the shared cache so BeautyScan.jsx can seed newly-created scans
 * without waiting for the next `/beauty/scans/latest` round-trip.
 */

let cachedScan = null;          // last known scan or `null` before first load
let cacheStatus = "idle";       // "idle" | "loading" | "loaded" | "error"
let cacheError = null;
let inflight = null;            // shared Promise across simultaneous mounts
const subscribers = new Set();  // Set<setter>

function broadcast() {
  const snap = { scan: cachedScan, status: cacheStatus, error: cacheError };
  subscribers.forEach((cb) => cb(snap));
}

async function fetchScan() {
  if (inflight) return inflight;
  cacheStatus = "loading";
  broadcast();
  inflight = (async () => {
    try {
      const { data } = await api.get("/beauty/scans/latest");
      cachedScan = data.scan || null;
      cacheStatus = "loaded";
      cacheError = null;
    } catch (e) {
      cacheStatus = "error";
      cacheError = e;
    } finally {
      inflight = null;
      broadcast();
    }
  })();
  return inflight;
}

/** Seed the cache with a scan object without hitting the API — used by
 *  BeautyScan.jsx immediately after a successful POST /beauty/scan so the
 *  makeup / hair-beard / care modules never see the "no scan yet" flash. */
export function setLatestBeautyScan(scan) {
  cachedScan = scan || null;
  cacheStatus = "loaded";
  cacheError = null;
  broadcast();
}

export function useLatestBeautyScan() {
  const [state, setState] = useState({
    scan: cachedScan,
    // If we already have a cached scan we're done — otherwise we're loading
    // until the (possibly in-flight) fetch completes.
    status: cacheStatus === "loaded" ? "loaded" : "loading",
    error: cacheError,
  });

  useEffect(() => {
    subscribers.add(setState);
    // Keep the current cached snapshot in local state (React ignores identical objects).
    setState({ scan: cachedScan, status: cacheStatus, error: cacheError });
    // Only fetch if we've never fetched OR the previous attempt errored.
    if (cacheStatus === "idle" || cacheStatus === "error") {
      fetchScan();
    }
    return () => { subscribers.delete(setState); };
  }, []);

  const refresh = useCallback(async () => {
    await fetchScan();
  }, []);

  const setScan = useCallback((scan) => setLatestBeautyScan(scan), []);

  return {
    scan: state.scan,
    loading: state.status === "loading",
    error: state.error,
    refresh,
    setScan,
  };
}
